import React, { useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import {
  ANNOUNCEMENT_HIDE_PREFIX,
  ANNOUNCEMENT_SEEN_PREFIX,
  MAX_VISIBLE_ANNOUNCEMENT_STACK,
  isAnnouncementInValidPeriod,
  normalizeAnnouncementList,
  shouldShowAnnouncement,
} from '../../utils/announcementDisplay';
import './AnnouncementDialogs.css';

const AnnouncementDialogs = ({ target = 'coupon' }) => {
  const [announcementQueue, setAnnouncementQueue] = useState([]);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const currentAnnouncement = announcementQueue[0] ?? null;
  const stackAnnouncements = announcementQueue.slice(0, MAX_VISIBLE_ANNOUNCEMENT_STACK);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.ANNOUNCEMENTS_ACTIVE(target), {
          headers: { Accept: 'application/json' },
        });
        const result = await response.json();
        if (response.ok && result?.data) {
          const visible = normalizeAnnouncementList(result.data)
            .filter(isAnnouncementInValidPeriod)
            .filter(shouldShowAnnouncement);
          setAnnouncementQueue(visible);
        }
      } catch {
        // Ignore announcement fetch errors
      }
    };

    fetchAnnouncements();
  }, [target]);

  const handleDismiss = (hidePermanently = false) => {
    if (!currentAnnouncement?.id) {
      setAnnouncementQueue([]);
      return;
    }

    const payload = JSON.stringify({ updated_at: currentAnnouncement.updated_at });
    const key = `${hidePermanently ? ANNOUNCEMENT_HIDE_PREFIX : ANNOUNCEMENT_SEEN_PREFIX}${currentAnnouncement.id}`;

    if (hidePermanently) {
      localStorage.setItem(key, payload);
    } else {
      sessionStorage.setItem(key, payload);
    }

    setAnnouncementQueue((prev) => prev.slice(1));
    setDontShowAgain(false);
  };

  const renderDialog = (announcement, { isFront, stackIndex, totalCount, titleId }) => {
    const type = announcement.type || 'info';
    const stackClass = isFront
      ? 'ann-dialog--stack-front'
      : `ann-dialog--stack-back ann-dialog--stack-back-${stackIndex}`;

    return (
      <div
        key={announcement.id}
        className={`ann-dialog ann-dialog--${type} ${stackClass}`}
        role={isFront ? 'dialog' : 'presentation'}
        aria-modal={isFront ? 'true' : undefined}
        aria-labelledby={isFront ? titleId : undefined}
        aria-hidden={!isFront}
      >
        <div className="ann-dialog__header">
          <div className="ann-dialog__header-main">
            <div className="ann-dialog__meta">
              <p className="ann-dialog__label">お知らせ</p>
            </div>
          </div>
          {isFront && totalCount > 1 && (
            <span className="ann-dialog__counter">1 / {totalCount}</span>
          )}
        </div>

        <div className="ann-dialog__body">
          <h2 id={isFront ? titleId : undefined} className="ann-dialog__title">
            {announcement.title}
          </h2>
          <p className="ann-dialog__content">{announcement.content}</p>
        </div>

        {isFront && (
          <div className="ann-dialog__footer">
            <label className="ann-dialog__checkbox">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="ann-dialog__checkbox-input"
              />
              <span className="ann-dialog__checkbox-custom">
                <svg viewBox="0 0 24 24" className="ann-dialog__checkbox-icon">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor" />
                </svg>
              </span>
              <span>次回から表示しない</span>
            </label>
            <button
              type="button"
              className="ann-dialog__confirm-btn"
              onClick={() => handleDismiss(dontShowAgain)}
            >
              確認
            </button>
          </div>
        )}
      </div>
    );
  };

  if (announcementQueue.length === 0) {
    return null;
  }

  const titleId = 'announcement-dialog-title';

  return (
    <div className="ann-dialog__overlay">
      <div
        className={`ann-dialog__stack ann-dialog__stack--count-${Math.min(
          stackAnnouncements.length,
          MAX_VISIBLE_ANNOUNCEMENT_STACK
        )}`}
      >
        {stackAnnouncements
          .slice()
          .reverse()
          .map((announcement, reverseIndex) => {
            const stackIndex = stackAnnouncements.length - 1 - reverseIndex;
            const isFront = stackIndex === 0;
            return renderDialog(announcement, {
              isFront,
              stackIndex,
              totalCount: announcementQueue.length,
              titleId,
            });
          })}
      </div>
    </div>
  );
};

export default AnnouncementDialogs;
